import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { GoogleMap, LoadScript } from '@react-google-maps/api';
import env from 'lib/env';
import Search from 'models/search';
import { Location } from 'models/locations';

const { GOOGLE_MAPS_API_KEY } = env();
const DEFAULT_INITIAL_ZOOM = 15;

export class GoogleMapView extends Component {
  constructor(props) {
    super(props);
    this.map = null;
    this.triggerBoundsChange = this.triggerBoundsChange.bind(this);
    this.fitBoundsToResults = this.fitBoundsToResults.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (!this.map) return;

    const { currentSearch, locations, selectedLocation } = this.props;
    const prevSearch = prevProps.currentSearch;

    const destinationChanged = currentSearch.destination !== prevSearch.destination;
    const parkingTypeChanged = currentSearch.parkingType !== prevSearch.parkingType;
    const selectionChanged = selectedLocation !== prevProps.selectedLocation;
    const initialLoad = (!prevProps.locations || prevProps.locations.size === 0) && (locations && locations.size > 0);

    if (destinationChanged || parkingTypeChanged || selectionChanged || initialLoad) {
      this.fitBoundsToResults();
    }
  }

  fitBoundsToResults() {
    if (!this.map || !window.google) return;

    const { currentSearch, locations, selectedLocation } = this.props;
    const bounds = new window.google.maps.LatLngBounds();

    const currentLat = currentSearch.anchorLat;
    const currentLng = currentSearch.anchorLng;

    // 1. Include search destination / anchor
    bounds.extend({ lat: currentLat, lng: currentLng });

    if (selectedLocation) {
      // 2. Fit to selected location entrance + search destination
      const entrance = selectedLocation.entrances.first();
      if (entrance) {
        bounds.extend({ lat: entrance.lat, lng: entrance.lng });
      }
    } else if (locations && locations.size > 0) {
      // 3. Include nearby search results (limit to first 10 for ideal viewport zoom)
      let count = 0;
      locations.sortBy(l => l.distance).forEach((location) => {
        if (count >= 10) return;
        const entrance = location.entrances.get(0);
        if (entrance) {
          bounds.extend({ lat: entrance.lat, lng: entrance.lng });
          count++;
        }
      });
    }

    // Call fitBounds with Mapbox-aligned asymmetrical padding
    const padding = selectedLocation
      ? { top: 100, bottom: 25, left: 50, right: 100 }
      : { top: 75, bottom: 25, left: 25, right: 100 };

    const maxZoom = selectedLocation ? 18 : 16;
    this.map.setOptions({ maxZoom });

    this.map.fitBounds(bounds, padding);

    // Reset maxZoom once the camera transition is complete so the user can zoom manually
    window.google.maps.event.addListenerOnce(this.map, 'idle', () => {
      if (this.map) {
        this.map.setOptions({ maxZoom: null });
      }
    });
  }

  triggerBoundsChange() {
    if (!this.map || this.props.selectedLocation) return;

    const { currentSearch, boundsChange } = this.props;
    const bounds = this.map.getBounds();
    const center = this.map.getCenter();
    const zoomLevel = this.map.getZoom();

    if (!bounds || !center) return;

    const lat = center.lat();
    const lng = center.lng();

    // Avoid infinite loops by skipping updates if coordinates are unchanged
    const searchLat = currentSearch.lat || currentSearch.anchorLat;
    const searchLng = currentSearch.lng || currentSearch.anchorLng;
    const searchZoom = currentSearch.zoomLevel || DEFAULT_INITIAL_ZOOM;

    const latDiff = Math.abs(lat - searchLat);
    const lngDiff = Math.abs(lng - searchLng);
    const zoomDiff = Math.abs(zoomLevel - searchZoom);

    if (latDiff < 0.0001 && lngDiff < 0.0001 && zoomDiff < 0.1) {
      return;
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const mockBounds = {
      getNorthEast: () => ({ lat: ne.lat(), lng: ne.lng() }),
      getSouthWest: () => ({ lat: sw.lat(), lng: sw.lng() }),
    };

    boundsChange({
      bounds: mockBounds,
      lat,
      lng,
      zoomLevel,
      router: false,
    });
  }

  render() {
    const { currentSearch } = this.props;
    const lat = currentSearch.lat || currentSearch.anchorLat;
    const lng = currentSearch.lng || currentSearch.anchorLng;
    const zoom = currentSearch.zoomLevel || DEFAULT_INITIAL_ZOOM;

    return (
      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          mapContainerStyle={{ height: '100%', width: '100%' }}
          defaultCenter={{ lat, lng }}
          defaultZoom={zoom}
          onLoad={(map) => { 
            this.map = map; 
            this.fitBoundsToResults();
          }}
          onDragEnd={() => this.triggerBoundsChange()}
          onZoomChanged={() => this.triggerBoundsChange()}
          options={{
            disableDefaultUI: true,
            gestureHandling: 'cooperative',
          }}
        >
          {/* Markers/overlays omitted for Phase 1 */}
        </GoogleMap>
      </LoadScript>
    );
  }
}

GoogleMapView.propTypes = {
  currentSearch: PropTypes.instanceOf(Search).isRequired,
  selectedLocation: PropTypes.instanceOf(Location),
  locations: PropTypes.any,
  boundsChange: PropTypes.func.isRequired,
};

GoogleMapView.defaultProps = {
  selectedLocation: null,
  locations: null,
};

export default GoogleMapView;
